/**
 * A storage object.
 *
 * Access `localStorage` via the object's methods (e.g. `storage.get()`,
 * `storage.set()`,  etc.), and `sessionStorage` via the object's `session`
 * property.
 *
 * @example
 * ```js
 * import { storage } from "../modules";
 *
 * // Save a value to localStorage.
 * const myItem = { a: 1, b: "2" };
 * storage.set("my-item", myItem);
 *
 * // Retrieve a value from sessionStorage then remove it.
 * const myOtherItem = storage.session.get("my-other-item");
 * storage.session.remove("my-other-item");
 * ```
 */
const storage = Object.assign(new Storage(), {
  session: new Storage(sessionStorage),
});

export default storage;

/**
 * A basic wrapper around the `Storage` interface of the Web Storage API (e.g.
 * `localStorage`). The module provides:
 *
 *   - exception handling
 *   - serialization
 *   - namespacing
 *
 * Note that directly accessing localStorage and sessionStorage may be unsafe:
 * in certain contexts a SecurityError exception will be thrown. For details,
 * see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage#exceptions | the description on MDN}.
 *
 * @param {globalThis.Storage} [storageObject] The storage object to use, or `window.localStorage` if none is provided.
 */
function Storage(storageObject = window && window.localStorage) {
  /**
   * The storage key prefix.
   */
  const prefix = "_ifrs";

  /**
   * Returns a prefix and normalized key derived from the provided string.
   *
   * @param {string} key The storage entry key.
   * @returns {string} A normalized, namespaced entry key.
   */
  const getKey = (key) => (key.startsWith(prefix) ? key : `${prefix}_${key.toLowerCase()}`);

  /**
   * Retrieves the specified entry from storage or `undefined` if the entry
   * can't be retrieved.
   *
   * @param {string} key The entry key.
   * @returns {*} The entry value.
   */
  this.get = (key) => {
    try {
      return JSON.parse(storageObject.getItem(getKey(key)));
    } catch (error) {
      // Ignore
    }
    return undefined;
  };

  /**
   * Saves the provided entry to storage.
   *
   * @param {string} key The entry key.
   * @param {*} value The entry value.
   */
  this.set = (key, value) => {
    try {
      storageObject.setItem(getKey(key), JSON.stringify(value));
    } catch (error) {
      // Ignore
    }
  };

  /**
   * Removes the specified entry from storage.
   *
   * @param {string} key The entry key.
   */
  this.remove = (key) => {
    try {
      storageObject.removeItem(getKey(key));
    } catch (error) {
      // Ignore
    }
  };

  /**
   * Removes all namespaced entries from storage.
   */
  this.clear = () => {
    try {
      Object.keys(storageObject).forEach((key) => {
        if (key.startsWith(prefix)) {
          storageObject.removeItem(key);
        }
      });
      storageObject.clear();
    } catch (error) {
      // Ignore
    }
  };
}
